import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Plus,
  User,
  Calendar,
  Clock,
  MoreVertical,
  Edit2,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Link as LinkIcon,
  GripVertical,
  Tag,
  AlignLeft,
  Circle,
  Circle as CircleIcon,
  CheckCircle,
  ArrowUpCircle,
  LucideActivity,
  Activity,
  Lightbulb,
  Zap,
  Timer,
  TrendingUp,
  Target,
  Users,
  Sparkles
} from 'lucide-react';

import { Button } from "../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../ui/card.jsx";
import { Badge } from "../ui/badge.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar.jsx";
import { ScrollArea } from "../ui/scroll-area.jsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog.jsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.jsx";
import { Input } from "../ui/input.jsx";
import { Textarea } from "../ui/textarea.jsx";
import { Label } from "../ui/label.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select.jsx";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipTrigger 
} from "../ui/tooltip.jsx";
import { Progress } from "../ui/progress.jsx";

const COLUMN_CONFIG = {
  todo: { 
    title: 'A Fazer', 
    color: 'border-l-slate-400 border-t-slate-200 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100',
    gradient: 'from-slate-500 to-gray-600',
    icon: <Circle className="h-5 w-5" />,
    iconColor: 'text-slate-600',
    accentColor: 'bg-slate-500',
    lightAccent: 'bg-slate-100'
  },
  in_progress: { 
    title: 'Em Andamento', 
    color: 'border-l-blue-400 border-t-blue-200 bg-gradient-to-br from-blue-50 via-sky-50 to-blue-100',
    gradient: 'from-blue-500 to-sky-600',
    icon: <Zap className="h-5 w-5" />,
    iconColor: 'text-blue-600',
    accentColor: 'bg-blue-500',
    lightAccent: 'bg-blue-100'
  },
  review: { 
    title: 'Revisão', 
    color: 'border-l-amber-400 border-t-amber-200 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-100',
    gradient: 'from-amber-500 to-orange-600',
    icon: <Timer className="h-5 w-5" />,
    iconColor: 'text-amber-600',
    accentColor: 'bg-amber-500',
    lightAccent: 'bg-amber-100'
  },
  done: { 
    title: 'Concluído', 
    color: 'border-l-green-400 border-t-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-green-100',
    gradient: 'from-green-500 to-emerald-600',
    icon: <CheckCircle className="h-5 w-5" />,
    iconColor: 'text-green-600',
    accentColor: 'bg-green-500',
    lightAccent: 'bg-green-100'
  }
};

const TaskCard = ({ task, onUpdate, onDelete, index, team }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState(task);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSave = async () => {
    await onUpdate(task.id, editedTask);
    setIsEditing(false);
  };

  const getDueDateColor = () => {
    if (!task.due_date) return 'text-muted-foreground';
    const dueDate = new Date(task.due_date);
    const today = new Date();
    const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'text-red-500 animate-pulse';
    if (diffDays <= 2) return 'text-orange-500';
    return 'text-green-500';
  };

  const getAssignedUser = () => {
    return team.find(member => member.email === task.assigned_to || member.id === task.assigned_to);
  };

  const assignedUser = getAssignedUser();

  const getTimeAgo = () => {
    if (!task.created_at && !task.created_date) return null;
    
    const now = new Date();
    const created = new Date(task.created_at || task.created_date);
    const diffMs = now - created;
    
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (mins > 0) return `${mins}m`;
    return 'agora';
  };

  const priorityConfig = {
    low: {
      color: 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 border-blue-300',
      icon: <Activity className="w-3 h-3" />,
      label: 'Baixa'
    },
    medium: {
      color: 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-700 border-yellow-300',
      icon: <TrendingUp className="w-3 h-3" />,
      label: 'Média'
    },
    high: {
      color: 'bg-gradient-to-r from-red-100 to-red-200 text-red-700 border-red-300',
      icon: <Zap className="w-3 h-3" />,
      label: 'Alta'
    }
  };

  const priorityInfo = priorityConfig[task.priority] || priorityConfig.medium;

  return (
    <Draggable draggableId={String(task.id)} index={index}>
      {(provided, snapshot) => (
        <motion.div
          ref={provided.innerRef}
          {...provided.draggableProps}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ 
            opacity: 1, 
            y: 0, 
            scale: 1,
            rotateX: snapshot.isDragging ? 5 : 0,
            rotateY: snapshot.isDragging ? 5 : 0
          }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ 
            duration: 0.3,
            type: "spring",
            stiffness: 300,
            damping: 30
          }}
          whileHover={{ 
            scale: 1.02,
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)"
          }}
          className={`
            mb-3 last:mb-0
            ${snapshot.isDragging ? 'z-50 rotate-1' : ''}
          `}
        >
          <Card className={`
            group overflow-hidden border-0 shadow-lg
            ${snapshot.isDragging ? 'shadow-2xl ring-2 ring-primary/30 bg-white' : 'hover:shadow-xl'} 
            transition-all duration-500 
            ${task.status === 'done' ? 'bg-gradient-to-br from-green-50 to-emerald-50' : 'bg-gradient-to-br from-white to-gray-50'}
            backdrop-blur-sm border border-white/20
          `}>
            {/* Linha de status colorida */}
            <div className={`absolute top-0 left-0 w-full h-1 ${COLUMN_CONFIG[task.status]?.accentColor || 'bg-gray-500'} rounded-t-lg`}></div>
            
            <CardContent className="p-4 space-y-3">
              {isEditing ? (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                >
                  <Input
                    value={editedTask.title}
                    onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                    placeholder="Título da tarefa"
                    className="border-primary/30 focus-visible:ring-primary/20 bg-white/80"
                  />
                  <Textarea
                    value={editedTask.description}
                    onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                    placeholder="Descrição"
                    rows={3}
                    className="border-primary/30 focus-visible:ring-primary/20 bg-white/80"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        Responsável
                      </Label>
                      <Select
                        value={editedTask.assigned_to}
                        onValueChange={(value) => setEditedTask({ ...editedTask, assigned_to: value })}
                      >
                        <SelectTrigger className="h-8 text-sm bg-white/80">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {team.map((member) => (
                            <SelectItem key={member.email || member.id} value={member.email || member.id}>
                              <div className="flex items-center gap-2">
                                <Avatar className="h-5 w-5">
                                  <AvatarFallback className="text-xs">
                                    {(member.name || member.full_name || 'U').substring(0, 2).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                {member.name || member.full_name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        Prioridade
                      </Label>
                      <Select
                        value={editedTask.priority}
                        onValueChange={(value) => setEditedTask({ ...editedTask, priority: value })}
                      >
                        <SelectTrigger className="h-8 text-sm bg-white/80">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">
                            <div className="flex items-center gap-2">
                              <Activity className="w-3 h-3 text-blue-500" />
                              Baixa
                            </div>
                          </SelectItem>
                          <SelectItem value="medium">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-3 h-3 text-yellow-500" />
                              Média
                            </div>
                          </SelectItem>
                          <SelectItem value="high">
                            <div className="flex items-center gap-2">
                              <Zap className="w-3 h-3 text-red-500" />
                              Alta
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Data Limite
                    </Label>
                    <Input
                      type="date"
                      value={editedTask.due_date}
                      onChange={(e) => setEditedTask({ ...editedTask, due_date: e.target.value })}
                      className="h-8 text-sm border-primary/30 focus-visible:ring-primary/20 bg-white/80"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                      Cancelar
                    </Button>
                    <Button size="sm" onClick={handleSave} className="bg-gradient-to-r from-primary to-primary/80">
                      Salvar
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div 
                      {...provided.dragHandleProps} 
                      className="mt-1 cursor-grab opacity-30 group-hover:opacity-70 hover:opacity-100 transition-all duration-300 hover:scale-110"
                      role="button"
                      tabIndex={0}
                      aria-label="Drag handle"
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </div>
                    
                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className={`font-medium line-clamp-2 transition-all duration-300 ${task.status === 'done' ? 'line-through text-muted-foreground' : 'group-hover:text-primary'}`}>
                          {task.title}
                        </h3>
                        
                        <div className="flex items-center gap-1">
                          {task.priority && (
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Badge className={`text-xs px-2 py-1 h-6 border ${priorityInfo.color} font-medium`}>
                                {priorityInfo.icon}
                                <span className="ml-1">{priorityInfo.label}</span>
                              </Badge>
                            </motion.div>
                          )}
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-all duration-300">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </motion.div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => setIsEditing(true)}>
                                <Edit2 className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setIsExpanded(!isExpanded)}>
                                {isExpanded ? (
                                  <>
                                    <ChevronUp className="h-4 w-4 mr-2" />
                                    Minimizar
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="h-4 w-4 mr-2" />
                                    Expandir
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() => onDelete(task.id)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      {(task.description && isExpanded) && (
                        <motion.div 
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="bg-muted/20 p-3 rounded-lg border-l-2 border-primary/20"
                        >
                          <p className="text-sm text-muted-foreground">
                            {task.description}
                          </p>
                        </motion.div>
                      )}

                      <div className="flex items-center justify-between text-xs pt-2">
                        {assignedUser ? (
                          <Tooltip>
                            <TooltipTrigger>
                              <motion.div 
                                className="flex items-center gap-2 p-1 rounded-full bg-muted/20 hover:bg-muted/40 transition-colors"
                                whileHover={{ scale: 1.05 }}
                              >
                                <Avatar className="h-6 w-6 ring-2 ring-white shadow-md">
                                  <AvatarImage src={assignedUser.avatar_url} />
                                  <AvatarFallback className="text-[10px] bg-gradient-to-br from-primary/20 to-primary/10 text-primary">
                                    {(assignedUser.name || assignedUser.full_name || 'U').substring(0, 2).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-muted-foreground truncate max-w-[80px] font-medium">
                                  {(assignedUser.name || assignedUser.full_name || 'U').split(' ')[0]}
                                </span>
                              </motion.div>
                            </TooltipTrigger>
                            <TooltipContent>{assignedUser.name || assignedUser.full_name}</TooltipContent>
                          </Tooltip>
                        ) : (
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground bg-muted/20 hover:bg-muted/40"
                              onClick={() => setIsEditing(true)}
                            >
                              <User className="h-3 w-3 mr-1" />
                              Atribuir
                            </Button>
                          </motion.div>
                        )}

                        <div className="flex items-center gap-2">
                          {task.due_date && (
                            <motion.div 
                              className={`flex items-center gap-1 px-2 py-1 rounded-full bg-white/50 ${getDueDateColor()}`}
                              whileHover={{ scale: 1.05 }}
                            >
                              <Calendar className="h-3 w-3" />
                              <span className="font-medium">
                                {format(new Date(task.due_date), 'dd/MM')}
                              </span>
                            </motion.div>
                          )}
                          
                          {getTimeAgo() && (
                            <div className="text-[10px] text-muted-foreground bg-muted/20 px-2 py-1 rounded-full">
                              {getTimeAgo()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {task.dependencies?.length > 0 && (
                    <motion.div 
                      className="flex items-center gap-1 mt-2 text-xs text-muted-foreground bg-gradient-to-r from-muted/30 to-transparent px-3 py-2 rounded-lg border-l-2 border-orange-300"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      <LinkIcon className="h-3 w-3" />
                      <span>{task.dependencies.length} dependências</span>
                    </motion.div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </Draggable>
  );
};

const Column = ({ title, color, tasks, onDrop, onCreateTask, onUpdateTask, onDeleteTask, team }) => {
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'medium',
    due_date: '',
    status: title
  });

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) return;
    await onCreateTask({
      ...newTask,
      status: title
    });
    setNewTask({
      title: '',
      description: '',
      assigned_to: '',
      priority: 'medium',
      due_date: '',
      status: title
    });
    setIsAddingTask(false);
  };

  const config = COLUMN_CONFIG[title] || COLUMN_CONFIG.todo;
  
  // Calcular estatísticas da coluna
  const totalTasks = tasks.length;
  const highPriorityTasks = tasks.filter(t => t.priority === 'high').length;
  const overdueTasks = tasks.filter(t => {
    if (!t.due_date) return false;
    return new Date(t.due_date) < new Date();
  }).length;

  // Calcular progresso baseado na coluna
  const getColumnProgress = () => {
    if (title === 'done') return 100;
    if (title === 'review') return 75;
    if (title === 'in_progress') return 50;
    return 25;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: Object.keys(COLUMN_CONFIG).indexOf(title) * 0.1 }}
      className="flex flex-col h-full"
    >
      <Card className={`flex flex-col border-l-4 border-t-2 shadow-xl h-full ${color} backdrop-blur-sm`}>
        <CardHeader className="p-4 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <motion.div 
                className={`p-2 rounded-xl bg-gradient-to-r ${config.gradient} shadow-lg`}
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
              >
                <div className="text-white">
                  {config.icon}
                </div>
              </motion.div>
              <div>
                <CardTitle className={`text-sm font-bold ${config.iconColor}`}>
                  {config.title}
                </CardTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs h-5 px-2 bg-white/50 backdrop-blur-sm">
                    {totalTasks} tarefas
                  </Badge>
                  {highPriorityTasks > 0 && (
                    <Badge className="text-xs h-5 px-2 bg-red-100 text-red-700 border-red-200">
                      <Zap className="w-3 h-3 mr-1" />
                      {highPriorityTasks}
                    </Badge>
                  )}
                  {overdueTasks > 0 && (
                    <Badge className="text-xs h-5 px-2 bg-orange-100 text-orange-700 border-orange-200 animate-pulse">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {overdueTasks}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 bg-white/20 hover:bg-white/40 backdrop-blur-sm shadow-lg"
                onClick={() => setIsAddingTask(true)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </motion.div>
          </div>
          
          {/* Progress bar visual */}
          <div className="mt-3 space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Progresso da Coluna</span>
              <span>{getColumnProgress()}%</span>
            </div>
            <Progress value={getColumnProgress()} className="h-2 bg-white/30" />
          </div>
        </CardHeader>
        
        <CardContent className="p-2 pb-4 flex-1 bg-gradient-to-b from-transparent via-white/5 to-white/10">
          <ScrollArea className="h-[calc(100vh-22rem)]">
            <Droppable droppableId={String(title)}>
              {(provided, snapshot) => (
                <motion.div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`min-h-[200px] px-2 pb-2 pt-1 rounded-xl transition-all duration-500 ${
                    snapshot.isDraggingOver 
                      ? 'bg-gradient-to-b from-primary/10 to-primary/5 ring-2 ring-primary/20 scale-[1.02]' 
                      : ''
                  }`}
                  animate={{
                    scale: snapshot.isDraggingOver ? 1.02 : 1,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <AnimatePresence>
                    {tasks.filter(task => task && task.id).map((task, index) => (
                      <TaskCard
                        key={String(task.id)}
                        task={task}
                        index={index}
                        onUpdate={onUpdateTask}
                        onDelete={onDeleteTask}
                        team={team}
                      />
                    ))}
                  </AnimatePresence>
                  {provided.placeholder}
                  
                  {/* Estado vazio com animação */}
                  {tasks.length === 0 && !snapshot.isDraggingOver && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex flex-col items-center justify-center h-32 text-muted-foreground"
                    >
                      <motion.div
                        animate={{ 
                          y: [0, -5, 0],
                          opacity: [0.5, 1, 0.5]
                        }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        {config.icon}
                      </motion.div>
                      <p className="text-xs mt-2 text-center">
                        Nenhuma tarefa aqui<br />
                        <span className="text-[10px]">Arraste ou crie uma nova</span>
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </Droppable>
          </ScrollArea>
        </CardContent>

        <Dialog open={isAddingTask} onOpenChange={setIsAddingTask}>
          <DialogContent className="sm:max-w-[500px] bg-gradient-to-br from-white to-gray-50">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-gradient-to-r ${config.gradient}`}>
                  <div className="text-white">
                    {config.icon}
                  </div>
                </div>
                <div>
                  <span>Nova Tarefa em {config.title}</span>
                  <p className="text-sm text-muted-foreground font-normal mt-1">
                    Adicione detalhes da nova tarefa
                  </p>
                </div>
              </DialogTitle>
              <DialogDescription>
                Preencha os campos abaixo para criar uma nova tarefa no projeto.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 pt-4">
              <div>
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <Sparkles className="w-4 h-4" />
                  Título *
                </Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Digite o título da tarefa"
                  className="mt-2 bg-white/80"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <AlignLeft className="w-4 h-4" />
                  Descrição
                </Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Descreva a tarefa em detalhes"
                  rows={3}
                  className="mt-2 bg-white/80"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <Users className="w-4 h-4" />
                    Responsável
                  </Label>
                  <Select
                    value={newTask.assigned_to}
                    onValueChange={(value) => setNewTask({ ...newTask, assigned_to: value })}
                  >
                    <SelectTrigger className="mt-2 bg-white/80">
                      <SelectValue placeholder="Selecione um membro" />
                    </SelectTrigger>
                    <SelectContent>
                      {team.map((member) => (
                        <SelectItem key={member.email || member.id} value={member.email || member.id}>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">
                                {(member.name || member.full_name || 'U').substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span>{member.name || member.full_name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <Target className="w-4 h-4" />
                    Prioridade
                  </Label>
                  <Select
                    value={newTask.priority}
                    onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
                  >
                    <SelectTrigger className="mt-2 bg-white/80">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">
                        <div className="flex items-center gap-2">
                          <Activity className="w-4 h-4 text-blue-500" />
                          Baixa
                        </div>
                      </SelectItem>
                      <SelectItem value="medium">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-yellow-500" />
                          Média
                        </div>
                      </SelectItem>
                      <SelectItem value="high">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-red-500" />
                          Alta
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <Calendar className="w-4 h-4" />
                  Data Limite
                </Label>
                <Input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  className="mt-2 bg-white/80"
                />
              </div>
            </div>
            <DialogFooter className="mt-6 flex gap-3">
              <Button variant="outline" onClick={() => setIsAddingTask(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateTask}
                className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                disabled={!newTask.title.trim()}
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Tarefa
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    </motion.div>
  );
};

export default function ProjectKanban({ tasks = [], onTaskUpdate, onCreateTask, team = [] }) {
  // Validar e limpar dados das tarefas
  const validTasks = tasks.filter(task => {
    if (!task || !task.id) {
      console.warn('📋 Tarefa inválida removida:', task);
      return false;
    }
    return true;
  });

  console.log('📋 Kanban: carregadas', validTasks.length, 'tarefas válidas de', tasks.length, 'total');
  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // Validações básicas
    if (!destination || !draggableId) {
      console.warn('📋 Drag and drop: destino ou draggableId inválido');
      return;
    }

    // Se não houve mudança de posição
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Encontrar a tarefa
    const task = validTasks.find(t => String(t.id) === draggableId);
    if (!task) {
      console.warn('📋 Drag and drop: tarefa não encontrada para ID:', draggableId);
      return;
    }

    console.log('📋 Drag and drop: movendo tarefa', task.title, 'para', destination.droppableId);

    try {
      const newStatus = destination.droppableId;
      await onTaskUpdate(task.id, { ...task, status: newStatus });
      console.log('✅ Drag and drop: tarefa movida com sucesso');
    } catch (error) {
      console.error('❌ Erro no drag and drop:', error);
    }
  };

  // Estatísticas gerais
  const totalTasks = validTasks.length;
  const completedTasks = validTasks.filter(t => t.status === 'done').length;
  const overallProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header com estatísticas */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-primary via-blue-600 to-purple-600 rounded-2xl p-6 text-white shadow-2xl"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Target className="w-7 h-7" />
              Kanban do Projeto
            </h2>
            <p className="text-primary-foreground/80">
              Gerencie tarefas de forma visual e eficiente
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{overallProgress}%</div>
            <div className="text-sm text-primary-foreground/80">Progresso Geral</div>
            <div className="w-32 bg-white/20 rounded-full h-2 mt-2">
              <motion.div 
                className="bg-white rounded-full h-2"
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 1, delay: 0.5 }}
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 h-[calc(100vh-18rem)]">
          {Object.entries(COLUMN_CONFIG).map(([key, config], index) => (
            <Column
              key={key}
              title={key}
              color={config.color}
              tasks={validTasks.filter(t => t.status === key)}
              onCreateTask={onCreateTask}
              onUpdateTask={onTaskUpdate}
              onDeleteTask={(id) => {
                if (confirm('Tem certeza que deseja excluir esta tarefa?')) {
                  onTaskUpdate(id, { deleted: true });
                }
              }}
              team={team}
            />
          ))}
        </div>
      </DragDropContext>
    </div>
  );
}